import { useEffect, useState } from "react";
import { Check, FileImage, Sparkles, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, newRef, today, useLoad, useSubmit } from "@/lib/api";
import { ErrorBox, Field, Modal } from "@/components/common";

export function BillLink({ fileId, label = "View bill" }) {
  const open = async () => {
    const w = window.open("", "_blank");
    try { const r = await api.get(`/files/${fileId}`, { responseType: "blob" }); w.location.href = URL.createObjectURL(r.data); }
    catch (e) { w?.close(); toast.error(errMsg(e, "Could not open the bill")); }
  };
  return <button type="button" className="link-btn" onClick={open} data-testid={`view-bill-${fileId}`}><FileImage size={13} /> {label}</button>;
}

export default function ExpenseForm({ onClose, onSaved }) {
  const { data: meta } = useLoad("/expenses/meta", { categories: ["Other"], methods: ["Cash"] });
  const [form, setForm] = useState({ date: today(), vendor: "", category: "Other", amount: "", method: "Cash", from_drawer: true, description: "", bill_file_id: "" });
  const [clientRef] = useState(newRef);
  const [preview, setPreview] = useState("");
  const [reading, setReading] = useState("");
  const { busy, error, run } = useSubmit();
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const upload = async (file) => {
    if (!file) return;
    if (file.type.startsWith("image/")) setPreview(URL.createObjectURL(file));
    setReading("Uploading bill…");
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/expenses/bills", fd);
      set("bill_file_id")(up.data.id);
      if (!file.type.startsWith("image/")) { setReading("PDF stored. Please type the details."); return; }
      setReading("Reading the bill…");
      const r = await api.post(`/expenses/bills/${up.data.id}/read`);
      const s = r.data;
      setForm((f) => ({ ...f, vendor: s.vendor || f.vendor, amount: s.amount ?? f.amount, date: /^\d{4}-\d{2}-\d{2}$/.test(s.date) ? s.date : f.date, category: s.category || f.category, description: s.description || f.description }));
      setReading("Details suggested from the bill. Please check them before saving.");
    } catch (e) { setReading(errMsg(e, "Could not read the bill. Please type the details.")); }
  };
  const save = () => run(async () => {
    const r = await api.post("/expenses", { ...form, amount: Number(form.amount), client_ref: clientRef });
    toast.success(r.data.from_drawer ? "Expense saved and taken from the cash drawer" : "Expense saved"); onSaved(r.data);
  });
  return (
    <Modal eyebrow="EXPENSES" title="Log an expense" onClose={onClose} onSubmit={save} testid="expense-form" wide>
      <label className="bill-drop" data-testid="bill-upload-area">
        {preview ? <img src={preview} alt="Bill preview" /> : <span><Upload size={20} /> Add a bill photo or PDF (optional)</span>}
        <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" capture="environment" onChange={(e) => upload(e.target.files?.[0])} data-testid="bill-file-input" />
      </label>
      {reading && <div className="status-message" data-testid="bill-read-status"><Sparkles size={15} /> {reading}</div>}
      <div className="form-two">
        <Field label="Amount (₹)" type="number" min="0.01" step="0.01" required testid="expense-amount-input" value={form.amount} onChange={set("amount")} />
        <Field label="Date" type="date" required testid="expense-date-input" value={form.date} onChange={set("date")} />
      </div>
      <div className="form-two">
        <Field label="Vendor" testid="expense-vendor-input" value={form.vendor} onChange={set("vendor")} />
        <Field label="Category" testid="expense-category-select" value={form.category} onChange={set("category")} options={meta.categories} />
      </div>
      <Field label="What was it for?" required testid="expense-description-input" value={form.description} onChange={set("description")} />
      <Field label="Paid by" testid="expense-method-select" value={form.method} onChange={set("method")} options={meta.methods} />
      {form.method === "Cash" && <label className="check-row"><input type="checkbox" checked={form.from_drawer} onChange={(e) => set("from_drawer")(e.target.checked)} data-testid="expense-drawer-checkbox" /> Paid from the clinic cash drawer (shows in the cashbook)</label>}
      <ErrorBox error={error} testid="expense-form-error" />
      <button className="primary full" disabled={busy || reading.endsWith("…")} data-testid="save-expense-button"><Check size={17} /> {busy ? "Saving…" : "Confirm & save expense"}</button>
    </Modal>
  );
}
